import React from 'react'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {ShieldCheck, Users, LogIn, RefreshCw, CheckCircle, Download} from 'lucide-react'

export default function WordPressIntegrationPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4" />
            <span>Official Licensed Plugin</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            WHMCS WordPress Integration
          </h1>
          
          <p className="text-xl text-slate-600 mb-8">
            Seamlessly sync users, handle logins, and bridge WHMCS with WordPress. Complete user synchronization and single sign-on solution.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
              Purchase Now - $99
            </button>
            <button className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all">
              View Documentation
            </button>
          </div>

          <div className="flex items-center justify-center space-x-2 text-sm text-slate-600">
            <Download className="w-4 h-4" />
            <span>Instant download • Lifetime updates included</span>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Key Features
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">User Synchronization</h3>
              <p className="text-slate-600">
                Automatically sync user accounts between WHMCS and WordPress. No manual data entry required.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Single Sign-On (SSO)</h3>
              <p className="text-slate-600">
                Users log in once and access both WHMCS and WordPress seamlessly without re-authentication.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Automatic Updates</h3>
              <p className="text-slate-600">
                Profile changes sync automatically. Email, password, and user data stay consistent across platforms.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Role Mapping</h3>
              <p className="text-slate-600">
                Map WHMCS client groups to WordPress roles for precise access control and permissions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            How It Works
          </h2>

          <div className="space-y-6">
            <div className="flex items-start space-x-4 bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Install & Configure</h3>
                <p className="text-slate-600">
                  Install the plugin on your WordPress site and configure the connection to your WHMCS installation with API credentials.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4 bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Sync Existing Users</h3>
                <p className="text-slate-600">
                  Run the initial synchronization to match existing WHMCS clients with WordPress users. The plugin handles duplicates intelligently.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4 bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Enable Auto-Login</h3>
                <p className="text-slate-600">
                  Activate SSO functionality. Users who log into WHMCS are automatically logged into WordPress and vice versa.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4 bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Enjoy Seamless Integration</h3>
                <p className="text-slate-600">
                  All future registrations, logins, and profile updates sync automatically. Your users experience a unified platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Why Choose Our Integration?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Save Time & Reduce Errors</h3>
              <p className="text-slate-600">
                Eliminate manual user management. Automatic synchronization means no data entry mistakes or outdated information.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Better User Experience</h3>
              <p className="text-slate-600">
                Your customers enjoy seamless access across your website and billing portal without multiple logins.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Secure & Reliable</h3>
              <p className="text-slate-600">
                Official licensed code with regular security updates. No vulnerabilities from unverified sources.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Bridge WHMCS and WordPress?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start providing a seamless experience for your customers today.
          </p>
          <button className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all hover:shadow-xl">
            Purchase Now - $99
          </button>
        </div>
      </section>

      <Footer />
    </div>
  )
}
